import * as Sentry from '@sentry/react';

const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN;
const ENVIRONMENT = import.meta.env.VITE_SENTRY_ENVIRONMENT || 'development';

// Import consent manager to check analytics consent
let consentManager: any = null;
import('../services/consentManager').then(module => {
  consentManager = module.consentManager;
}).catch(() => {
  // ConsentManager not available - fallback to basic initialization
});

/**
 * Detect if we're running in an actual browser extension context
 * This is more strict than Sentry's internal check to avoid false positives from PWAs
 */
const isInExtensionContext = (): boolean => {
  // Check for extension protocols (most reliable)
  if (
    window.location.protocol === 'chrome-extension:' ||
    window.location.protocol === 'moz-extension:' ||
    window.location.protocol === 'ms-browser-extension:'
  ) {
    return true;
  }

  // Check if we have chrome.runtime.id (definitive extension indicator)
  // Note: chrome.runtime exists in normal pages if extension is installed, but .id doesn't
  try {
    const chromeAPI = (globalThis as any).chrome;
    if (typeof chromeAPI !== 'undefined' && chromeAPI.runtime && chromeAPI.runtime.id) {
      return true;
    }
  } catch (e) {
    // chrome.runtime.id throws in normal web pages
  }

  // Check for Firefox extension APIs
  try {
    const browserAPI = (globalThis as any).browser;
    if (typeof browserAPI !== 'undefined' && browserAPI.runtime && browserAPI.runtime.id) {
      return true;
    }
  } catch (e) {
    // browser.runtime.id throws in normal web pages
  }

  return false;
};

// Initialize Sentry only if DSN is provided and not a placeholder
export const initSentry = () => {
  // Skip if DSN not configured
  if (!SENTRY_DSN || SENTRY_DSN.includes('your_sentry_dsn_here')) {
    console.warn('Sentry DSN not configured properly. Skipping Sentry initialization.');
    return;
  }

  // Enhanced browser extension environment detection
  if (isInExtensionContext()) {
    console.log('Browser extension environment detected. Skipping Sentry initialization.');
    return;
  }

  // Check consent before initializing enhanced features
  const hasAnalyticsConsent = consentManager?.hasConsentFor('analytics') || false;
  
  // Workaround: Temporarily hide chrome.runtime to prevent Sentry's extension detection
  // This is safe because we've already verified we're not in an extension context
  const originalChromeRuntime = (globalThis as any).chrome?.runtime;
  const hasChromeRuntime = !!(globalThis as any).chrome?.runtime;
  
  if (hasChromeRuntime && originalChromeRuntime) {
    // Temporarily set chrome.runtime to undefined during Sentry.init
    try {
      delete (globalThis as any).chrome.runtime;
    } catch (e) {
      // If we can't delete it, try to set it to undefined
      (globalThis as any).chrome.runtime = undefined;
    }
  }
  
  try {
    Sentry.init({
      dsn: SENTRY_DSN,
      environment: ENVIRONMENT,
      
      integrations: [
        Sentry.browserTracingIntegration(),
        // Only include session replay if user consented to analytics
        ...(hasAnalyticsConsent ? [Sentry.replayIntegration()] : [])
      ],

    // Performance Monitoring
    tracesSampleRate: ENVIRONMENT === 'production' ? 0.1 : 1.0,

    // Set sample rate for profiling - this is relative to tracesSampleRate
    profilesSampleRate: 1.0,

    // Only enable session replay if user consented
    replaysSessionSampleRate: hasAnalyticsConsent ? 0.1 : 0,
    replaysOnErrorSampleRate: hasAnalyticsConsent ? 1.0 : 0,

    beforeSend(event, hint) {
      // Filter based on consent
      if (!hasAnalyticsConsent && event.exception) {
        // Only send critical errors without consent
        const error = hint.originalException;
        if (error && error instanceof Error && error.message && !error.message.toLowerCase().includes('critical')) {
          return null;
        }
      }

      // Filter out some errors that are not actionable
      if (event.exception) {
        const error = hint.originalException;
        
        // Filter out network errors that might be temporary
        if (error && typeof error === 'object' && 'message' in error) {
          const errorMessage = (error as Error).message.toLowerCase();
          if (errorMessage.includes('network error') || 
              errorMessage.includes('fetch') ||
              errorMessage.includes('timeout')) {
            return null; // Don't send to Sentry
          }
        }
      }

      return event;
    },

      // Set user context
      initialScope: {
        tags: {
          component: 'frontend',
          framework: 'react'
        }
      }
    });
    
    console.log(`Sentry initialized for environment: ${ENVIRONMENT} with analytics consent: ${hasAnalyticsConsent}`);
  } finally {
    // Restore chrome.runtime after Sentry initialization
    if (hasChromeRuntime && originalChromeRuntime) {
      try {
        (globalThis as any).chrome.runtime = originalChromeRuntime;
      } catch (e) {
        // Ignore errors restoring chrome.runtime
      }
    }
  }
};

// Initialize Sentry with consent awareness
export const initSentryWithConsent = () => {
  // Initial setup
  initSentry();
  
  // Re-initialize when consent changes (if consent manager is available)
  if (consentManager) {
    consentManager.onConsentChange((preferences: any) => {
      if (preferences.analytics) {
        // Re-initialize with full features
        console.log('[Sentry] Analytics consent granted, re-initializing with enhanced features');
        initSentry();
      } else {
        console.log('[Sentry] Analytics consent withdrawn, Sentry will use basic error reporting only');
        // Sentry continues to work but without replay features
      }
    });
  }
};

// Helper to set user context
export const setSentryUser = (user: {
  id?: string;
  email?: string;
  username?: string;
  subscription_tier?: string;
}) => {
  Sentry.setUser({
    id: user.id,
    email: user.email,
    username: user.username,
    subscription_tier: user.subscription_tier
  });
};

// Helper to add breadcrumb
export const addSentryBreadcrumb = (message: string, category?: string, level?: Sentry.SeverityLevel) => {
  Sentry.addBreadcrumb({
    message,
    category: category || 'custom',
    level: level || 'info',
    timestamp: Date.now() / 1000
  });
};

// Helper to capture exception
export const captureSentryException = (error: Error, context?: Record<string, any>) => {
  Sentry.withScope((scope) => {
    if (context) {
      Object.keys(context).forEach(key => {
        scope.setContext(key, context[key]);
      });
    }
    Sentry.captureException(error);
  });
};

// Helper to capture message
export const captureSentryMessage = (message: string, level?: Sentry.SeverityLevel) => {
  Sentry.captureMessage(message, level || 'info');
};

export default Sentry;
